import { useState } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { leadApi, ApiError } from '../services/api'
import confetti from 'canvas-confetti'
import { 
  User, 
  Building, 
  Mail, 
  Phone, 
  MapPin, 
  CreditCard,
  CheckCircle,
  ArrowRight,
  Loader2
} from 'lucide-react'

const GetStartedPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    companyUrl: '',
    province: '',
    city: '',
    paymentVolume: '',
    businessType: '',
    paymentSolution: '',
    additionalInfo: ''
  })

  // Confetti celebration function
  const triggerConfetti = () => {
    // Multiple confetti bursts for a party effect
    const duration = 3000
    const end = Date.now() + duration

    const colors = ['#ff6b35', '#f7931e', '#ffd23f', '#06d6a0', '#118ab2']

    function frame() {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      })
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }

    // Initial burst from center
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    })

    // Start the continuous side bursts
    frame()
  }

  const [step, setStep] = useState(1)
  const totalSteps = 3
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const submitLeadToAPI = async (leadData: typeof formData) => {
    try {
      // Prepare data according to backend schema
      const apiData = {
        fullName: leadData.fullName,
        email: leadData.email,
        phone: leadData.phone,
        province: leadData.province,
        city: leadData.city,
        companyName: leadData.companyName,
        companyUrl: leadData.companyUrl || undefined,
        businessType: leadData.businessType,
        paymentVolume: leadData.paymentVolume,
        paymentSolution: leadData.paymentSolution,
        additionalInfo: leadData.additionalInfo,
      }

      return await leadApi.submitLead(apiData)
    } catch (error) {
      console.error('API Error:', error)
      
      // Handle API errors with validation details
      if (error instanceof ApiError && error.errors) {
        const errorMessages = Object.entries(error.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ')
        throw new Error(`Validation errors: ${errorMessages}`)
      }
      
      throw error
    }
  }

  const validateStep = (currentStep: number): boolean => {
    const errors: string[] = []
    const safeNameRegex = /^[\p{L}][\p{L}\s.-]{0,79}$/u
    const safeTextRegex = /^[\p{L}\p{N}\s.,&()/-]{2,500}$/u
    const safeEmailRegex = /^[A-Z0-9](?:[A-Z0-9._%+-]{0,62}[A-Z0-9])?@[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?)+$/i
    
    if (currentStep === 1) {
      if (!formData.fullName.trim()) errors.push('Full name is required')
      if (!formData.email.trim()) errors.push('Email is required')
      if (!formData.phone.trim()) errors.push('Phone number is required')
      if (!formData.province) errors.push('Province is required')
      if (!formData.city.trim()) errors.push('City is required')
      
      if (formData.fullName && !safeNameRegex.test(formData.fullName.trim())) {
        errors.push('Please enter a valid full name')
      }
      if (formData.email && !safeEmailRegex.test(formData.email.trim())) {
        errors.push('Please enter a valid email address')
      }
      
      // Basic phone validation
      if (formData.phone && formData.phone.length < 10) {
        errors.push('Phone number must be at least 10 digits')
      }
    }
    
    if (currentStep === 2) {
      if (!formData.companyName.trim()) errors.push('Company name is required')
      if (formData.companyName && !safeTextRegex.test(formData.companyName.trim())) {
        errors.push('Company name contains invalid characters')
      }
      if (!formData.businessType) errors.push('Business type is required')
      if (!formData.paymentVolume) errors.push('Payment volume is required')
      if (!formData.paymentSolution) errors.push('Payment solution is required')
    }
    
    if (currentStep === 3) {
      if (!formData.additionalInfo.trim()) errors.push('Additional information is required')
      if (formData.additionalInfo && !safeTextRegex.test(formData.additionalInfo.trim())) {
        errors.push('Additional information contains invalid characters')
      }
    }
    
    if (errors.length > 0) {
      setSubmitStatus({
        type: 'error',
        message: errors.join(', ')
      })
      return false
    }
    
    setSubmitStatus({ type: null, message: '' })
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate current step
    if (!validateStep(step)) {
      return
    }
    
    if (step < totalSteps) {
      // Move to next step
      setStep(step + 1)
      return
    }

    // Final submission
    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: '' })

    try {
      const result = await submitLeadToAPI(formData)
      
      // Trigger confetti celebration! 🎉
      triggerConfetti()
      
      setSubmitStatus({
        type: 'success',
        message: result.message || 'Thank you! Your application has been submitted successfully. Our team will contact you within 24 hours.'
      })

      // Optional: Reset form after successful submission
      // You can uncomment the next few lines if you want to reset the form
      // setFormData({
      //   fullName: '', email: '', phone: '', companyName: '', companyUrl: '',
      //   province: '', city: '', paymentVolume: '', businessType: '', 
      //   paymentSolution: '', additionalInfo: ''
      // })
      // setStep(1)
      
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Something went wrong. Please try again.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const provinces = [
    'Punjab',
    'Sindh',
    'Khyber Pakhtunkhwa',
    'Balochistan',
    'Islamabad Capital Territory',
    'Gilgit-Baltistan',
    'Azad Jammu & Kashmir'
  ]

  const businessTypes = [
    'E-commerce',
    'Retail',
    'Services',
    'Software/SaaS',
    'Education',
    'Healthcare',
    'Restaurant/Food',
    'Travel & Tourism',
    'Non-profit',
    'Other'
  ]

  const paymentSolutions = [
    'Payment Gateway',
    'Point of Sale (POS)',
    'Mobile Payment App',
    'Recurring Billing',
    'Invoice & Billing',
    'All Solutions'
  ]

  const benefits = [
    {
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      title: "Quick Setup",
      description: "Get started in just 15 minutes with our streamlined onboarding"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      title: "No Hidden Fees",
      description: "Transparent pricing with no setup fees or monthly charges"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      title: "24/7 Support",
      description: "Dedicated support team to help you every step of the way"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      title: "Secure & Compliant",
      description: "Bank-level security with fraud protection and end-to-end encryption on every transaction"
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="gradient-bg text-white py-16">
        <div className="container-max section-padding">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Get Started with MobiPay
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of businesses already using MobiPay to accept payments. 
              Fill out the form below and we'll have you up and running in no time.
            </p>
            <div className="flex justify-center items-center space-x-4">
              <div className="text-sm bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                ✅ No setup fees
              </div>
              <div className="text-sm bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                ✅ Start earning in 24 hours
              </div>
              <div className="text-sm bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                ✅ Dedicated support
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Benefits Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Why Choose MobiPay?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Join the digital payment revolution with Pakistan's most trusted payment platform.
                  </p>
                </div>

                <div className="space-y-6">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 mt-1">
                        {benefit.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {benefit.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-primary-50 rounded-lg p-6">
                  <h4 className="font-semibold text-primary-900 mb-2">
                    Need Help?
                  </h4>
                  <p className="text-sm text-primary-700 mb-4">
                    Our team is here to help you get started
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2 text-primary-700">
                      <Phone className="h-4 w-4" />
                      <span>+92 343 8018055</span>
                    </div>
                    <div className="flex items-center space-x-2 text-primary-700">
                      <Mail className="h-4 w-4" />
                      <span>support@mobipay.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Registration Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Step {step} of {totalSteps}
                    </span>
                    <span className="text-sm text-gray-500">
                      {Math.round((step / totalSteps) * 100)}% Complete
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(step / totalSteps) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {step === 1 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">
                        Personal Information
                      </h2>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="text"
                              name="fullName"
                              value={formData.fullName}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                              placeholder="Enter your full name"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                              placeholder="Enter your email"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number *
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                              placeholder="+92 343 8018055"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Province *
                          </label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <select
                              name="province"
                              value={formData.province}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none bg-white"
                              required
                            >
                              <option value="">Select Province</option>
                              {provinces.map((province) => (
                                <option key={province} value={province}>
                                  {province}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="Enter your city"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">
                        Business Information
                      </h2>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Company Name *
                          </label>
                          <div className="relative">
                            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="text"
                              name="companyName"
                              value={formData.companyName}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                              placeholder="Enter company name"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Company Website
                          </label>
                          <input
                            type="url"
                            name="companyUrl"
                            value={formData.companyUrl}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                            placeholder="https://yourwebsite.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Business Type *
                          </label>
                          <select
                            name="businessType"
                            value={formData.businessType}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none bg-white"
                            required
                          >
                            <option value="">Select Business Type</option>
                            {businessTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Monthly Payment Volume *
                          </label>
                          <div className="relative">
                            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <select
                              name="paymentVolume"
                              value={formData.paymentVolume}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none bg-white"
                              required
                            >
                              <option value="">Select Volume</option>
                              <option value="under-100k">Under ₨ 1 Lakh</option>
                              <option value="100k-500k">₨ 1-5 Lakh</option>
                              <option value="500k-1m">₨ 5-10 Lakh</option>
                              <option value="1m-5m">₨ 10-50 Lakh</option>
                              <option value="above-5m">Above ₨ 50 Lakh</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Solution Needed *
                        </label>
                        <select
                          name="paymentSolution"
                          value={formData.paymentSolution}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none bg-white"
                          required
                        >
                          <option value="">Select Payment Solution</option>
                          {paymentSolutions.map((solution) => (
                            <option key={solution} value={solution}>
                              {solution}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">
                        Additional Information
                      </h2>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tell us about your business needs *
                        </label>
                        <textarea
                          name="additionalInfo"
                          value={formData.additionalInfo}
                          onChange={handleInputChange}
                          rows={6}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="Tell us about your specific requirements, expected transaction volume, any questions you might have..."
                          required
                        />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                            <span className="text-gray-700">We'll review your application within 24 hours</span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                            <span className="text-gray-700">Our team will contact you to discuss your needs</span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">3</div>
                            <span className="text-gray-700">Setup your account and start accepting payments</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Messages */}
                  {submitStatus.type && (
                    <div className={`p-6 rounded-lg ${
                      submitStatus.type === 'success' 
                        ? 'bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 text-green-800 shadow-lg' 
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                      <div className="flex items-start">
                        {submitStatus.type === 'success' ? (
                          <div className="flex-shrink-0 mr-3">
                            <div className="text-2xl">🎉</div>
                          </div>
                        ) : (
                          <div className="h-5 w-5 mr-2 mt-0.5">⚠️</div>
                        )}
                        <div className="flex-1">
                          {submitStatus.type === 'success' ? (
                            <>
                              <h3 className="font-bold text-lg text-green-900 mb-2">
                                🎊 Congratulations! Your application has been submitted! 🎊
                              </h3>
                              <p className="text-green-800">{submitStatus.message}</p>
                              <div className="mt-3 text-sm text-green-700 bg-green-100 rounded-lg p-3">
                                <strong>What's next?</strong> Our team is already reviewing your application and will reach out within 24 hours to get you started with MobiPay! 🚀
                              </div>
                              <div className="mt-3 text-sm text-blue-700 bg-blue-100 rounded-lg p-3 flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                                </svg>
                                <span><strong>📧 Email Confirmation:</strong> A detailed confirmation email has been sent to your registered email address with your application summary.</span>
                              </div>
                            </>
                          ) : (
                            <p>{submitStatus.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Form Navigation */}
                  <div className="flex justify-between pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setStep(Math.max(1, step - 1))}
                      className={`px-6 py-3 font-semibold rounded-lg transition-colors ${
                        step === 1 || isSubmitting
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      disabled={step === 1 || isSubmitting}
                    >
                      Previous
                    </button>
                    
                    {step < totalSteps ? (
                      <button
                        type="button"
                        onClick={() => setStep(step + 1)}
                        className="btn-primary flex items-center"
                        disabled={isSubmitting}
                      >
                        Next Step
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className={`btn-primary flex items-center ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Application
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default GetStartedPage
